import React, { useEffect, useReducer } from 'react';
import { useChatContext } from 'stream-chat-react';
import { FixedSizeList } from 'react-window';
import Fuse from 'fuse.js';
import { nanoid } from 'nanoid';
import { Button, IconButton, Tooltip, Dialog,
  Box, Checkbox, Chip, Divider, Grid, InputAdornment, ListItem,
  ListItemButton, ListItemIcon, ListItemAvatar, Badge, Avatar,
  ListItemText, Typography, DialogActions, DialogContent,
  DialogTitle, Slide, TextField } from '@mui/material';
import { AddCircleOutline as AddChannelIcon, Clear as ClearIcon, Search as SearchIcon, Warning as WarningIcon } from '@mui/icons-material';
import { getServiceUsers } from '../../utils';

const Transition = React.forwardRef((props, ref) => <Slide direction='down' ref={ref} {...props} />);
const initialState_modal = {
  open: false,
  data: { clientId: '', users: [], users_filtered: [], selected: {}, ch_name: '', input: '', filter: '' },
  error: false,
  errorType: ''
};
const reducer_modal = (state, action) => {
  switch (action.type) {
    case 'UPDATE':
      return { ...state, data: {...state.data, ...action.payload } };
    case 'UPDATE-SELECTED':
      if (Object.keys(state.data.selected).includes(action.payload.id) === true) delete state.data.selected[action.payload.id];
      else state.data.selected[action.payload.id] = action.payload;
      return { ...state,  data: { ...state.data } };
    case 'UPDATE-FILTER':
      let new_filter = state.data.filter === action.payload.filter ? '' : action.payload.filter;
      return { ...state, data: { ...state.data, filter: new_filter } };
    case 'UPDATE-CHNAME':
      return { ...state, errorType: '', data: { ...state.data, ch_name: action.payload.ch_name } };
    case 'ERROR':
      return { ...state, error: true };
    case 'ERROR-CHNAME':
      return { ...state, errorType: action.payload };
    case 'OPEN':
      return { ...initialState_modal, open: true, data: { ...initialState_modal.data, ...action.payload } };
    case 'CLOSE': 
      return { ...initialState_modal };
    default:
      return state;
  }
};

export const TeamAddModal = () => {
  const { client, setActiveChannel } = useChatContext();
  const [state_modal, dispatch_modal] = useReducer(reducer_modal, initialState_modal);
  useEffect(() => {
    if (state_modal.open === true && state_modal.data.clientId !== '') {
      try {
        let tmp_input = state_modal.data.input;
        let tmp_filter = state_modal.data.filter;
        let tmp_usersArray = state_modal.data.users;
        if (tmp_filter === 'online') tmp_usersArray = tmp_usersArray.filter(itu => itu.online === true);
        if (tmp_input !== '') {
          const options = {
            includeScore: true,
            includeMatches: true,
            threshold: 0.1,
            keys: [ 'id', 'name', 'roles' ]
          };
          const fuseInit = new Fuse(tmp_usersArray, options);
          const fuseResult = fuseInit.search(tmp_input);
          tmp_usersArray = fuseResult.map(res => res.item);
        }
        dispatch_modal({type: 'UPDATE', payload: { users_filtered: tmp_usersArray }});
      }
      catch (error) {
        console.log(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state_modal.open, state_modal.data.clientId, state_modal.data.input, state_modal.data.filter])
  const clickOpenModal = async () => {
    try {
      const serviceUsers = await getServiceUsers(client);
      const dispatchPayload = { clientId: client.userID, users: [...serviceUsers], selected: {} };
      dispatch_modal({type: 'OPEN', payload: dispatchPayload});
    } catch (error) {
      console.log(error);
      dispatch_modal({type: 'ERROR'});
    }
  };
  const clickCloseModal = () => dispatch_modal({type: 'CLOSE'});
  const onChangeChName = (e) => dispatch_modal({type: 'UPDATE-CHNAME', payload: { ch_name: e.target.value.replace(/^\s+/g, '').replace(/\s\s+/g, ' ') } });
  const onChangeInput = (e) => dispatch_modal({type: 'UPDATE', payload: { input: e.target.value } });
  const clickCreate = async (e) => {
    try {
      e.preventDefault();
      if (state_modal.data.selected === null || typeof state_modal.data.selected !== 'object' || Array.isArray(state_modal.data.selected) === true) return dispatch_modal({type: 'ERROR'});
      if (Object.keys(state_modal.data.selected).length < 2) return dispatch_modal({type: 'ERROR'});
      if (typeof state_modal.data.ch_name !== 'string') return dispatch_modal({type: 'ERROR-CHNAME', payload: 'Invalid name'});
      if (state_modal.data.ch_name.length === 0) return dispatch_modal({type: 'ERROR-CHNAME', payload: 'Name is required'});
      if (state_modal.data.ch_name.length <= 3) return dispatch_modal({type: 'ERROR-CHNAME', payload: 'Name is too short'});
      if (state_modal.data.ch_name.length > 40) return dispatch_modal({type: 'ERROR-CHNAME', payload: 'Name is too long'});
      let uniqueChannelId = 'group-' + client.userID + nanoid();
      let ch_name = state_modal.data.ch_name.replace(/^\s+/g, '').replace(/\s\s+/g, ' ');
      let ch_members = [client.userID, ...Object.keys(state_modal.data.selected)];
      const newChannel = client.channel('team', uniqueChannelId, { name: ch_name, members: ch_members });
      await newChannel.watch({ messages: { limit: 25 } });
      setActiveChannel(newChannel);
      dispatch_modal({type: 'CLOSE'});
    } catch (error) {
      console.log(error)
      dispatch_modal({type: 'ERROR'});
    }
  };

  return (
    <>
    <IconButton color='primary' sx={{ p: '10px', mr: 0.5 }} aria-label='new channel' onClick={clickOpenModal}>
      <Tooltip title='New Channel' placement='bottom'><AddChannelIcon /></Tooltip>
    </IconButton>
    <Dialog fullWidth={true} maxWidth='sm' open={state_modal.open} TransitionComponent={Transition} onClose={clickCloseModal} aria-describedby='group-add-dialog'>
      <DialogTitle>
        <Grid container direction='row' justifyContent='flex-start' alignItems='center' spacing={0}>
        <AddChannelIcon color='primary' sx={{mr: 2}} /><Typography component='div' variant='h6' textAlign='left' color='primary'>{'New group'}</Typography>
        </Grid>
      </DialogTitle>
      <DialogContent sx={{p:0, m:0, minHeight: 420}}>
        <Divider orientation='horizontal'/>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', m: 0, py: 2, px: 2}}>
          <TextField
            label='Search'
            size='small'
            variant='outlined'
            onChange={onChangeInput}
            fullWidth={true}
            value={state_modal.data.input}
            InputProps={{
              startAdornment: <InputAdornment position='start'><IconButton color='primary' onClick={() => dispatch_modal({type: 'UPDATE', payload: {input: ''}})} edge='start'>{state_modal.data.input === '' ? <SearchIcon /> : <ClearIcon />}</IconButton></InputAdornment>
            }}
          />
          <Box sx={{ flex: '1 1 auto', minWidth: '8px' }} />
          <Box sx={{ minWidth: '58px', textAlign: 'center'}}>
            <Chip size='small' label={'Online'} color='primary' variant={state_modal.data.filter === 'online' ? 'filled' : 'outlined'} onClick={() => dispatch_modal({type: 'UPDATE-FILTER', payload: {filter: 'online'}})}/>
          </Box>
        </Box>
        <Divider orientation='horizontal'/>
        {/* LIST HERE */}
        <FixedSizeList height={300} width={'100%'} itemSize={60} itemData={state_modal.data.users_filtered} itemCount={state_modal.data.users_filtered.length} overscanCount={4}>
          {(listProps) => {
            const { data, index, style } = listProps;
            const item = data[index];
            const itemKey = item.id;
            const isSelected = state_modal.data.selected !== null && Object.keys(state_modal.data.selected).includes(itemKey) === true;
            const toggleSelectMember = () => dispatch_modal({type: 'UPDATE-SELECTED', payload: item });
            return (
              <>
                <ListItem style={style} key={itemKey} component='div' disablePadding>
                  <ListItemButton role={undefined} sx={{height: '100%'}} onClick={toggleSelectMember} selected={isSelected} /*disabled={}*/>
                    <ListItemIcon sx={{minWidth: '36px', width: '36px'}}>
                      <Checkbox edge='start' checked={isSelected} tabIndex={-1} disableRipple inputProps={{ 'aria-labelledby': itemKey }}/>
                    </ListItemIcon>
                    <ListItemAvatar>
                      <Badge color={ item.online === true ? 'success' : 'error'} variant='dot' overlap='circular' anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} sx={item.online === false ? {'& .MuiBadge-dot' : { visibility: 'hidden' }} : {}}>
                        <Avatar sx={{textTransform: 'capitalize'}}>{item.name.charAt(0)}</Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography noWrap={true} sx={{ display: 'block' }} fontWeight={400}>{item.name}</Typography>}
                      secondary={<Typography noWrap={true} sx={{ display: 'block' }} component='span' color='grey.500' variant='body2' fontStyle='italic'>{item.roles?.length > 0 ? item.roles.map(role => role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()).join(', ') : 'No role'}</Typography>}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            );
          }}
        </FixedSizeList>
        <Divider orientation='horizontal'/>
        <Box sx={{ height: '20px', textAlign: 'center'}}>
          { state_modal.data.selected !== null && Object.keys(state_modal.data.selected).length > 0
          && <><Typography color={Object.keys(state_modal.data.selected).length === 1 ? 'warning.main' : 'info.main'} component='div' variant='body1' sx={{fontStyle: 'italic', textAlign: 'center', background: 'rgba(0, 154, 201, 0.125)', py: 1 }}>
            {`${Object.keys(state_modal.data.selected).length}/${state_modal.data.users.length} selected`}
            </Typography>
            <Divider orientation='horizontal'/>
            </>
          }
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', m: 0, py: 2, px: 2}}>
        { state_modal.error === false && state_modal.data.selected !== null && Object.keys(state_modal.data.selected).length >= 2 &&<TextField
            label='Name the new group'
            size='small'
            variant='outlined'
            onChange={onChangeChName}
            fullWidth={true}
            value={state_modal.data.ch_name}
            helperText={state_modal.errorType !== '' ? state_modal.errorType : ' '}
            error={state_modal.errorType !== ''}
          /> }
        <Box sx={{ display: 'flex', flexDirection: 'row', m: 0, py: 2, px: 2}}>
        <Box sx={{ flex: '1 1 auto' }} />
        { state_modal.error === true && <Button size='small' variant='outlined' endIcon={<WarningIcon />} color={'error'} sx={{mr: 1}}>{'Error'}</Button> }
        { state_modal.error === false && state_modal.data.selected !== null && Object.keys(state_modal.data.selected).length >= 2 && <Button size='small' variant='outlined' color={'primary'} onClick={clickCreate} sx={{mr: 1}}>{'Create'}</Button> }
        <Button size='small' variant='text' onClick={clickCloseModal}>{'Close'}</Button>
        </Box>
      </DialogActions>
    </Dialog>
    </>
  );
};